import { css, keyframes } from 'styled-components'
import { getP18 } from './FontSizes'
import { getTransition, mq, vw } from './config'
import type { Variants } from 'motion/react'

export type TypeThemeColors = 'background' | 'navbar-background' | 'creme' | 'orange' | 'img-placeholder' | 'grey_dark' | 'grey_medium' | 'grey_light' | 'text' | 'text-reverse' | 'grey' | 'white' | 'black' | 'input-base' | 'input-error' | 'warning'

type Props = {
  height?:number
  reverse?: boolean
  color?: string
}

const underlineReappear = (height) => keyframes`
  from {
    background-size: 0% ${height}px;
    background-position: 0 97%;
  }
  to {
    background-size: 100% ${height}px;
    background-position: 0 97%;
  }
`

const underlineDisappear = (height) => keyframes`
  from {
    background-size: 100% ${height}px;
    background-position: 0 97%;
  }
  to {
    background-size: 0 ${height}px;
    background-position: 100% 97%;
  }
`

export const createUnderline = ({ height = 1, reverse = false,  color = 'var(--color-text)' }:Props) => css`
  background-image: linear-gradient(${color}, ${color});
  background-position: ${reverse ? '100% 97%' : '0 97%'};
  background-repeat: no-repeat;
  background-size: ${`${reverse ? 100 : 0}% ${height}px`};
  position: relative;
  transition: all 250ms ease;

  &:hover,
  &.active {
    background-size: ${`${reverse ? 0 : 100}% ${height}px`};
  }

  &:not(:hover) {
    animation: ${reverse ? underlineReappear(height) : underlineDisappear(height)} 250ms ease;
  }
`

const underlineContract = (height: number) => keyframes`
  0% { background-size: 100% ${height}px; }
  100% { background-size: 0% ${height}px; }
`

export const createUnderlineMiddle = ({ height = 1, color = 'var(--color-text)' }: Props) => css`
  background-image: linear-gradient(${color}, ${color});
  background-position: center 97%;
  background-repeat: no-repeat;
  background-size: 0% ${height}px;
  position: relative;
  transition: background-size 300ms ease;

  &:hover,
  &.active {
    background-size:100% ${height}px;
  }

  &:not(:hover) {
    animation: ${underlineContract(height)} 300ms ease; /* Use different animations based on reverse */
  }
`

export type AlphaStepNumber = 0 | .05 | .1 | .15 | .2 | .25 | .3 | .35 | .4 | .45 | .5 | .55 | .6 | .65 | .7 | .75 | .8 | .85 | .9 | .95 | 1
export function hex2Rgba(hex: string, alpha:AlphaStepNumber) {
  if (!/^#[0-9A-Fa-f]{6}$/i.test(hex)) console.warn('The hexadecimal color provider is not valid')
  const r = parseInt(hex.substring(1, 3), 16)
  const g = parseInt(hex.substring(3, 5), 16)
  const b = parseInt(hex.substring(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export const rgbAlpha = (color:string, opacity:AlphaStepNumber) => css`${`color-mix(in srgb, ${`var(${color})`} ${opacity*100}%, transparent);`}`

export const hideScrollbar = () => css`
  -ms-overflow-style: none;  /* Internet Explorer 10+ */
  scrollbar-width: none;  /* Firefox */

  &::-webkit-scrollbar {
    background-color: var(--color-text);
    display: none;
  }
`

const defaultPlaceHolderStyles = (color?:string) => css`
  transition: opacity 300ms ease-out;
  color: ${color ?? 'var(--color-text)'};
`

export const parsePlaceHolder = (styles, color = null) => (css`
  &::-webkit-input-placeholder { /* Chrome/Opera/Safari */
    ${defaultPlaceHolderStyles(color)}
    ${styles}
  }

  &::-moz-placeholder { /* Firefox 19+ */
    ${defaultPlaceHolderStyles(color)}
    ${styles}
  }
  &:-ms-input-placeholder { /* IE 10+ */
    ${defaultPlaceHolderStyles(color)}
    ${styles}
  }

  &:-moz-placeholder { /* Firefox 18- */
    ${defaultPlaceHolderStyles(color)}
    ${styles}
  }
`)


export const FigmaAnimations = {
  gentle: {
    damping: 15,
    mass: 1,
    stiffness: 100,
    type: 'spring'
  },
  quick: {
    damping: 20,
    mass: 1,
    stiffness: 300,
    type: 'spring'
  },
  bouncy: {
    damping: 15,
    mass: 1,
    stiffness: 600,
    type: 'spring'
  },
  slow: {
    damping: 20,
    mass: 1,
    stiffness: 80,
    type: 'spring'
  }
}


export const getVariants = (inView) => {
  const variants = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: inView ? 1 : 0, y: 0, delay: .3 },
    exit: { opacity: 0, y: 10 },
    transition: { duration: 1, ease: 'easeInOut' }
  }
  return variants
}


export const getButtonUnderline = ({color = 'black'} = {}) => {
  return css`
    ${getP18()}
    color: var(--color-${color});
    position: relative;

    a {
      color: var(--color-${color});
      display: inline-block;
    }

    span {
      position: absolute;
      bottom: 0;
      height: 0.03rem;
      opacity: 0.4;
      width: 0;
      background-color: var(--color-${color});
      left: unset;
      right: 0;
      transition: width 300ms ease, left 300ms ease, right 300ms ease;
      bottom: -${vw(4, 'mobile')};

      ${mq.greaterThan('tablet')}{
        bottom: -${vw(4, 'desktop')};
        opacity: 0.2;
      }
    }

    &:hover,
    &.active {
      span {
        left: 0;
        right: unset;
        width: 100%;
      }
    }
  `
}

export const getImgTransition = () => {
  return css`
    opacity: 0;
    transform: scale(1.05);
    transition:  transform 1s cubic-bezier(0.32, 0, 0.29, 0.99) .3s, opacity 1s cubic-bezier(0.32, 0, 0.29, 0.99) .3s;

    &.ld {
      opacity: 1;
      transform: scale(1);
    }

  `
}

export const getInvertedButtonUnderline = ({color = 'black'} = {}) => {
  return css`
    ${getP18()}
    color: var(--color-${color});
    position: relative;

    a {
      color: var(--color-${color});
      display: inline-block;
    }

    span {
      position: absolute;
      bottom: 0;
      height: 0.03rem;
      opacity: 0.4;
      width: 100%;
      background-color: var(--color-${color});
      left: 0;
      right: unset;
      transition: width 300ms ease, left 300ms ease, right 300ms ease;
      bottom: -${vw(4, 'mobile')};

      ${mq.greaterThan('tablet')}{
        bottom: -${vw(4, 'desktop')};
        opacity: 0.2;
      }
    }

    &:hover,
    &.active {
      span {
        left: unset;
        right: 0;
        width: 0;
      }
    }

    &:disabled {
      opacity: .4;
    }
  `
}

export const getRegularButton = () => {
  return css`
    ${getP18()}
    color: var(--color-white);
    background-color: var(--color-black);
    height: ${vw(40, 'mobile')};
    border-radius: ${vw(20, 'mobile')};
    padding: 0 ${vw(40, 'mobile')} ${vw(5, 'mobile')};

    ${mq.greaterThan('tablet')}{
      height: ${vw(40, 'desktop')};
      border-radius: ${vw(20, 'desktop')};
      padding: 0 ${vw(40, 'desktop')} ${vw(5, 'desktop')};
    }

    &:hover {
      background-color: ${rgbAlpha('--color-black', .7)};
    }

    &:disabled {
      background-color: ${rgbAlpha('--color-black', .4)};
    }

    a {
      color: var(--color-white);
      display: inline-block;
    }
  `
}


export const imgVariants:Variants = {
  initial: {
    scale: 1.05,
    opacity: 0,
    transition: {
      ...getTransition()
    }
  },
  animate: {
    scale: 1,
    opacity: 1,
    transition: {
      ...getTransition(),
      ease: [0.32, 0, 0.29, 0.99],
      delay: .3,
      duration: 1
    }
  },
  exit: {
    scale: 1.05,
    opacity: 0,
    transition: {
      ...getTransition(),
      duration: .2
    }
  },
}

export const staggerWrapperVariants:Variants = {
  animate: {
    transition: {
      staggerChildren: 0.5,
    }
  },
}

export const staggerChildVariants:Variants = {
  initial: {
    y: '20px',
    opacity: 0,
    transition: {
      ...getTransition()
    }
  },
  animate: {
    y: '0px',
    opacity: 1,
    transition: {
      ...getTransition(),
      ease: [.32,0,.29,.99],
      duration: .5,
    }
  },
}

export const titleVariants:Variants = {
  initial: {
    y: '20px',
    opacity: 0,
    transition: {
      ...getTransition()
    }
  },
  animate: {
    y: '0px',
    opacity: 1,
    transition: {
      ...getTransition(),
      ease: [.32,0,.29,.99],
      delay: .5,
      duration: 1
    }
  },
  exit: {
    y: '20px',
    opacity: 0,
    transition: {
      ...getTransition(),
      duration: .5
    }
  }
}

export const basicVariantsProps = {
  'initial': 'initial',
  'animate': 'animate',
  'exit': 'exit',
}

